// Route optimization ("Optimisation de tournée", #23500) — pure scheduling core, no iframe access.
//
// One day is optimized at a time (a visit never changes day, RG-02). The caller (agendaRouteSource.js)
// turns the legacy appointments into plain items and resolves a shared travel-time matrix; this module
// only orders the movable store visits and lays them out in time.
//
// Item roles (RG-06, RG-22):
// - 'visit'    geolocated store visit the optimizer may reorder and reschedule
// - 'pinned'   store visit pinned by the user: keeps its slot, travel to/from it still counts
// - 'locked'   store visit the user can't move (validated, in progress, past, not author): same as pinned
// - 'hc'       Hors Clientèle appointment: blocks time, no travel to/from it (RG-07)
// - 'external' Outlook event: same as 'hc'
// - 'kept'     (internal) visit that couldn't be placed, kept on its current slot (RG-21): same as pinned
// A store visit without coordinates can't be routed to, so it only blocks time — and is never overlapped.
//
// Times are epoch milliseconds, durations[i][j] are minutes between matrix points i and j.

const MINUTE = 60000;
const EXHAUSTIVE_MAX = 8;
// Simulations allowed per day to also try every position of the visits around the fixed visits (about the
// cost of the 8! orders alone, ~70 ms on a desktop); past it, local search places them.
const EXHAUSTIVE_BUDGET = 50000;

export const STEP_KIND = { VISIT: 'visit', PINNED: 'pinned', LOCKED: 'locked', UNPLACEABLE: 'unplaceable', BLOCK: 'block' };
export const MOVE = { EARLIER: 'earlier', LATER: 'later', UNCHANGED: 'unchanged' };

// '02h35', same format as the agenda day header (agenda.js dayHeaderUpdate).
export const formatDuration = (minutes) => {
	const total = Math.max(0, Math.round(minutes || 0));
	return `${String(Math.floor(total / 60)).padStart(2, '0')}h${String(total % 60).padStart(2, '0')}`;
};

// One travel leg: '45 min' under an hour, '1h35' above (same as MapLibre / WidgetMyDay route durations).
export const formatMinutes = (minutes) => {
	const total = Math.max(0, Math.round(minutes || 0));
	return total < 60 ? `${total} min` : `${Math.floor(total / 60)}h${String(total % 60).padStart(2, '0')}`;
};

// Travel gain as shown in the summary (RG-16): '−45 min', '−2h35', '+10 min', '0 min'.
export const formatGain = (gainMinutes) => {
	const value = Math.round(gainMinutes || 0);
	if (!value) return '0 min';
	return `${value > 0 ? '−' : '+'}${formatMinutes(Math.abs(value))}`;
};

// Rounds up on the local agenda grid (minutes since local midnight), not on the epoch: a UTC+05:30 user
// with a 60 min subdivision gets 09:00, not 09:30.
export const roundUpToStep = (ms, stepMinutes) => {
	if (!stepMinutes) return ms;
	const step = stepMinutes * MINUTE;
	const d = new Date(ms);
	const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
	return midnight + Math.ceil((ms - midnight) / step) * step;
};

const hasPoint = (item) => item.point !== null && item.point !== undefined;

const solverKind = (item) => {
	if (item.role === 'visit') return hasPoint(item) ? 'movable' : 'block';
	if ((item.role === 'pinned' || item.role === 'locked' || item.role === 'kept') && hasPoint(item)) return 'fixed';
	return 'block';
};

const travelMinutes = (durations, from, to) => {
	if (from === null || from === undefined || to === null || to === undefined || from === to) return 0;
	const value = durations?.[from]?.[to];
	return Number.isFinite(value) ? value : 0;
};

/**
 * Time window the movable visits must fit in (RG-11, RG-12, RG-13).
 * Day start = first store visit, or the BO morning limit when "keep start" is off (never later than the
 * current start). Visits are never scheduled in the past.
 * `limit` is the hard end: day start + BO working time (capped by the end of the day), never before the
 * current last visit. The working time counts from the start of the day, even when "now" is later.
 * `end` is the targeted end: the current last visit when "keep end" is on — a soft objective (RG-13) the
 * solver tries to meet but may exceed up to `limit` — else `limit` itself.
 */
export function computeWindow(items, { keepStart = true, keepEnd = true } = {}, { earliestStart = null, maxWorkMinutes = null, dayEnd = null } = {}, now = null, stepMinutes = 15) {
	const stores = items.filter((i) => i.isStore);
	if (!stores.length) return null;

	const firstStart = Math.min(...stores.map((s) => s.start));
	const lastEnd = Math.max(...stores.map((s) => s.end));

	const dayStart = keepStart || earliestStart === null ? firstStart : Math.min(firstStart, earliestStart);
	const start = now !== null ? Math.max(dayStart, roundUpToStep(now, stepMinutes)) : dayStart;

	const workEnd = maxWorkMinutes ? dayStart + maxWorkMinutes * MINUTE : dayEnd;
	const limit = workEnd === null ? lastEnd : Math.max(lastEnd, dayEnd !== null ? Math.min(workEnd, dayEnd) : workEnd);

	return { start, end: keepEnd ? lastEnd : limit, limit };
}

function pushPastBlocks(start, duration, blocks, stepMinutes) {
	let s = start;
	let moved = true;
	while (moved) {
		moved = false;
		for (const b of blocks) {
			if (s < b.end && s + duration > b.start) {
				s = roundUpToStep(b.end, stepMinutes);
				moved = true;
			}
		}
	}
	return s;
}

/**
 * Lays out the movable visits in the given order around the fixed store visits and the blocks.
 * Travel between consecutive store visits is never compressed (RG-08): a visit can't start before
 * "end of previous + travel", which also rules out any store/store overlap (RG-09). HC/external blocks are
 * avoided when possible (they block time, RG-07) but overlapping one is tolerated (RG-09) rather than
 * leaving the visit out; a store visit without coordinates is never overlapped. A visit may end after the
 * targeted day end (counted as overflow, RG-13) but one that doesn't fit before the hard limit is left out
 * (unplaceable, RG-14).
 * A fixed visit that can't be reached in time counts as a violation (soft, so a result is always produced).
 *
 * `cuts[j]` is the position in `order` before which fixed visit j is reached: the visits before it are laid
 * out before the fixed visit, the others after it. Without cuts, a visit goes before the next fixed visit
 * whenever it fits there.
 */
export function simulateOrder(order, ctx, cuts = null) {
	const { durations, window, fixed, blocks, storeBlocks = [], startPoint, endPoint, stepMinutes } = ctx;
	const timeline = [];
	const unplaceable = [];
	let violations = 0;
	let blockOverlaps = 0;
	let free = null;
	let loc = startPoint ?? null;
	let fixedIndex = 0;
	let travel = 0;

	const visitFixed = () => {
		const f = fixed[fixedIndex++];
		const leg = travelMinutes(durations, loc, f.point);
		if (free !== null && free + leg * MINUTE > f.start) violations++;
		timeline.push({ item: f, start: f.start, end: f.end, leg: loc === null ? 0 : leg });
		travel += loc === null ? 0 : leg;
		free = f.end;
		loc = f.point;
	};

	const restore = (snapshot) => {
		timeline.length = snapshot.timelineLength;
		({ violations, free, loc, fixedIndex, travel } = snapshot);
	};

	const tryPlace = (v, avoidBlocks) => {
		const duration = v.end - v.start;
		while (true) {
			const leg = travelMinutes(durations, loc, v.point);
			const ready = free === null ? window.start : Math.max(window.start, free + leg * MINUTE);
			// The day start is kept as planned even off the agenda subdivision; later starts snap to it.
			const earliest = ready === window.start ? ready : roundUpToStep(ready, stepMinutes);
			const s = pushPastBlocks(earliest, duration, avoidBlocks ? blocks : storeBlocks, stepMinutes);
			const nextFixed = fixed[fixedIndex];

			if (nextFixed && s + duration + travelMinutes(durations, v.point, nextFixed.point) * MINUTE > nextFixed.start) {
				visitFixed();
				continue;
			}

			if (s + duration > window.limit) return false;

			timeline.push({ item: v, start: s, end: s + duration, leg: loc === null ? 0 : leg });
			travel += loc === null ? 0 : leg;
			free = s + duration;
			loc = v.point;
			return true;
		}
	};

	for (const [position, v] of order.entries()) {
		while (cuts && fixedIndex < fixed.length && cuts[fixedIndex] <= position) visitFixed();
		const snapshot = { timelineLength: timeline.length, violations, free, loc, fixedIndex, travel };
		if (tryPlace(v, true)) continue;
		restore(snapshot);
		if (tryPlace(v, false)) {
			blockOverlaps++;
			continue;
		}
		restore(snapshot);
		unplaceable.push(v);
	}

	while (fixedIndex < fixed.length) visitFixed();

	const endLeg = endPoint !== null && endPoint !== undefined && loc !== null ? travelMinutes(durations, loc, endPoint) : 0;
	travel += endLeg;

	const lastEnd = timeline.length ? Math.max(...timeline.map((s) => s.end)) : window.start;
	const overflow = Math.max(0, lastEnd - window.end) / MINUTE;
	const displacement = timeline.reduce((sum, s) => sum + Math.abs(s.start - s.item.start) / MINUTE, 0);

	return { timeline, unplaceable, violations, blockOverlaps, overflow, travel, endLeg, lastEnd, displacement };
}

const compareCost = (a, b) => a.violations - b.violations || a.unplaceable.length - b.unplaceable.length || a.blockOverlaps - b.blockOverlaps || a.overflow - b.overflow || a.travel - b.travel || a.lastEnd - b.lastEnd || a.displacement - b.displacement;

function* permutations(items) {
	const a = items.slice();
	const c = new Array(a.length).fill(0);
	yield a.slice();
	let i = 0;
	while (i < a.length) {
		if (c[i] < i) {
			const j = i % 2 === 0 ? 0 : c[i];
			[a[j], a[i]] = [a[i], a[j]];
			yield a.slice();
			c[i]++;
			i = 0;
		} else {
			c[i] = 0;
			i++;
		}
	}
}

function nearestNeighbourOrder(movable, durations, startPoint) {
	const remaining = movable.slice();
	const order = [];
	let loc = startPoint ?? null;
	while (remaining.length) {
		let bestIndex = 0;
		if (loc !== null) {
			let best = Infinity;
			remaining.forEach((v, index) => {
				const d = travelMinutes(durations, loc, v.point);
				if (d < best) {
					best = d;
					bestIndex = index;
				}
			});
		}
		const [next] = remaining.splice(bestIndex, 1);
		order.push(next);
		loc = next.point;
	}
	return order;
}

const factorial = (n) => (n <= 1 ? 1 : n * factorial(n - 1));
const binomial = (n, k) => Array.from({ length: k }, (_, i) => i).reduce((r, i) => (r * (n - i)) / (i + 1), 1);

// Every way to reach `count` fixed visits along an order of `length` visits: non-decreasing cut positions.
function* cutPositions(length, count, from = 0, prefix = []) {
	if (prefix.length === count) {
		yield prefix;
		return;
	}
	for (let p = from; p <= length; p++) yield* cutPositions(length, count, p, [...prefix, p]);
}

// First-improvement local search: 2-opt (segment reversal) on the order, then each fixed visit moved along it.
function improve(order, cuts, ctx, current) {
	let improved = true;
	while (improved) {
		improved = false;
		for (let i = 0; i < order.length - 1; i++) {
			for (let k = i + 1; k < order.length; k++) {
				const candidate = [...order.slice(0, i), ...order.slice(i, k + 1).reverse(), ...order.slice(k + 1)];
				const result = simulateOrder(candidate, ctx, cuts);
				if (compareCost(result, current) < 0) {
					order = candidate;
					current = result;
					improved = true;
				}
			}
		}
		for (let j = 0; j < cuts.length; j++) {
			for (let p = j ? cuts[j - 1] : 0; p <= (j + 1 < cuts.length ? cuts[j + 1] : order.length); p++) {
				if (p === cuts[j]) continue;
				const candidate = cuts.slice();
				candidate[j] = p;
				const result = simulateOrder(order, ctx, candidate);
				if (compareCost(result, current) < 0) {
					cuts = candidate;
					current = result;
					improved = true;
				}
			}
		}
	}
	return { order, cuts, result: current };
}

function bestOrder(movable, ctx) {
	const original = movable.slice().sort((a, b) => a.start - b.start);
	const atEnd = ctx.fixed.map(() => original.length);
	let best = { order: original, cuts: atEnd, result: simulateOrder(original, ctx, atEnd) };

	if (original.length <= EXHAUSTIVE_MAX) {
		// Every order, and every position of each visit relative to the fixed ones while the budget allows;
		// past it, every order with the visits packed before the fixed ones, refined by local search.
		const everyCut = factorial(original.length) * binomial(original.length + ctx.fixed.length, ctx.fixed.length) <= EXHAUSTIVE_BUDGET;
		for (const order of permutations(original)) {
			for (const cuts of everyCut ? cutPositions(original.length, ctx.fixed.length) : [atEnd]) {
				const result = simulateOrder(order, ctx, cuts);
				if (compareCost(result, best.result) < 0) best = { order, cuts, result };
			}
		}
		return everyCut ? best.result : improve(best.order, best.cuts, ctx, best.result).result;
	}

	// Too many visits for an exhaustive search: chronological and nearest neighbour seeds + local search.
	for (const seed of [original, nearestNeighbourOrder(original, ctx.durations, ctx.startPoint)]) {
		const local = improve(seed, atEnd, ctx, simulateOrder(seed, ctx, atEnd));
		if (compareCost(local.result, best.result) < 0) best = local;
	}
	return best.result;
}

/**
 * Travel time of the day as currently planned: start point → store visits in chronological order →
 * end point, with the same matrix as the optimized proposal so "before → after" is comparable (RG-16).
 */
export function currentTravel(items, durations, startPoint = null, endPoint = null) {
	const stores = items.filter((i) => i.isStore && hasPoint(i)).sort((a, b) => a.start - b.start);
	const legs = new Map();
	let loc = startPoint ?? null;
	let travel = 0;
	for (const s of stores) {
		const leg = loc === null ? 0 : travelMinutes(durations, loc, s.point);
		legs.set(s.uid, leg);
		travel += leg;
		loc = s.point;
	}
	const endLeg = endPoint !== null && endPoint !== undefined && loc !== null ? travelMinutes(durations, loc, endPoint) : 0;
	return { travel: travel + endLeg, legs, endLeg };
}

/**
 * True when the day as planned already respects RG-08/RG-09 around its movable visits: no overlap and
 * enough time for the travel between consecutive geolocated visits. A conflict between two visits the
 * optimizer can't move (past, validated, pinned) is ignored: no proposal could fix it.
 */
export function isCurrentScheduleFeasible(items, durations) {
	const stores = items.filter((i) => i.isStore).sort((a, b) => a.start - b.start);
	for (let i = 1; i < stores.length; i++) {
		const prev = stores[i - 1];
		if (solverKind(prev) !== 'movable' && solverKind(stores[i]) !== 'movable') continue;
		const leg = hasPoint(prev) && hasPoint(stores[i]) ? travelMinutes(durations, prev.point, stores[i].point) : 0;
		if (stores[i].start < prev.end + leg * MINUTE) return false;
	}
	return true;
}

const moveOf = (newStart, oldStart) => (newStart < oldStart ? MOVE.EARLIER : newStart > oldStart ? MOVE.LATER : MOVE.UNCHANGED);

const stepKindOf = (item) => {
	if (item.role === 'visit') return hasPoint(item) ? STEP_KIND.VISIT : STEP_KIND.LOCKED;
	if (item.role === 'pinned') return STEP_KIND.PINNED;
	if (item.role === 'kept') return STEP_KIND.UNPLACEABLE;
	if (item.role === 'locked' && item.isStore) return STEP_KIND.LOCKED;
	return STEP_KIND.BLOCK;
};

/**
 * Optimizes one day.
 *
 * A visit that doesn't fit is "unplaceable" (RG-14): it is left out of the proposal and the user decides
 * (RG-21). "Supprimer" keeps the proposal as is. "Garder" keeps the visit on its current slot: it is passed
 * back in `settings.kept` and becomes a fixed visit the others are laid out around, so it is never
 * overlapped (RG-19) — which always leaves another visit out, else the visit would have been placed.
 *
 * @param {object} day
 * @param {string} day.date        'YYYY-MM-DD'
 * @param {object[]} day.items     { uid, label, address, start, end, point, isStore, role }
 * @param {number|null} day.startPoint  matrix index of the departure (hotel, else home), null = first visit (RG-18)
 * @param {number|null} day.endPoint    matrix index of the return point, null = none
 * @param {object} day.bounds      { earliestStart, maxWorkMinutes, dayEnd } (BO, RG-12)
 * @param {number[][]} durations   travel minutes between matrix points
 * @param {object} options         { keepStart, keepEnd }
 * @param {object} settings        { now, stepMinutes, kept: Set of the unplaceable visit uids kept on their slot }
 */
export function optimizeDay(day, durations, options = {}, { now = null, stepMinutes = 15, kept = null } = {}) {
	const items = (day.items || []).map((item) => (kept?.has(item.uid) && solverKind(item) === 'movable' ? { ...item, role: 'kept' } : item));
	const before = currentTravel(items, durations, day.startPoint, day.endPoint);
	const window = computeWindow(items, options, day.bounds || {}, now, stepMinutes);

	const base = { date: day.date, window, startPoint: day.startPoint ?? null, endPoint: day.endPoint ?? null, startKind: day.startKind ?? null, endKind: day.endKind ?? null, travelBefore: before.travel, beforeLegs: before.legs, beforeEndLeg: before.endLeg };

	const movable = items.filter((i) => solverKind(i) === 'movable');
	const unchanged = () => {
		const steps = items
			.slice()
			.sort((a, b) => a.start - b.start)
			.map((item) => ({ item, kind: stepKindOf(item), start: item.start, end: item.end, oldStart: item.start, oldEnd: item.end, moved: MOVE.UNCHANGED, legBefore: before.legs.get(item.uid) ?? null }));
		return { ...base, travelAfter: before.travel, endLeg: before.endLeg, steps, unplaceable: [], changes: [], endExceeded: false, reorderedCount: 0, pinnedCount: items.filter((i) => i.role === 'pinned').length };
	};
	if (!window || !movable.length) return unchanged();

	const fixed = items.filter((i) => solverKind(i) === 'fixed').sort((a, b) => a.start - b.start);
	const blocks = items.filter((i) => solverKind(i) === 'block').map((i) => ({ start: i.start, end: i.end, isStore: i.isStore }));
	const ctx = { durations, window, fixed, blocks, storeBlocks: blocks.filter((b) => b.isStore), startPoint: day.startPoint ?? null, endPoint: day.endPoint ?? null, stepMinutes };
	const best = bestOrder(movable, ctx);

	// A day that is already valid is left as planned rather than compacted when it can't be shortened in
	// travel, and never traded for a proposal that leaves one of its visits out.
	if ((best.travel >= before.travel || best.unplaceable.length > 0) && isCurrentScheduleFeasible(items, durations)) return unchanged();

	const routed = best.timeline.map((s) => ({ item: s.item, kind: stepKindOf(s.item), start: s.start, end: s.end, oldStart: s.item.start, oldEnd: s.item.end, moved: moveOf(s.start, s.item.start), legBefore: s.leg }));
	const unplacedIds = new Set(best.unplaceable.map((i) => i.uid));
	const others = items.filter((i) => solverKind(i) === 'block').map((item) => ({ item, kind: stepKindOf(item), start: item.start, end: item.end, oldStart: item.start, oldEnd: item.end, moved: MOVE.UNCHANGED, legBefore: null }));
	const steps = [...routed, ...others].filter((s) => !unplacedIds.has(s.item.uid)).sort((a, b) => a.start - b.start || a.end - b.end);
	const changes = routed.filter((s) => s.kind === STEP_KIND.VISIT && (s.start !== s.oldStart || s.end !== s.oldEnd)).map((s) => ({ uid: s.item.uid, start: s.start, end: s.end }));

	return {
		...base,
		travelAfter: best.travel,
		endLeg: best.endLeg,
		steps,
		unplaceable: best.unplaceable,
		changes,
		endExceeded: best.unplaceable.length > 0 || best.overflow > 0,
		reorderedCount: routed.filter((s) => s.kind === STEP_KIND.VISIT && s.moved !== MOVE.UNCHANGED).length,
		pinnedCount: items.filter((i) => i.role === 'pinned').length,
	};
}

/**
 * Timeline of one optimized day for the calendar preview: the stops plus the travel legs between routed
 * store visits (RG-16). 'after' = the proposal (unplaceable visits excluded, kept ones on their slot),
 * 'before' = the day as planned.
 * Legs sit right after the previous visit; the first leg ends at the first visit (departure, RG-18).
 *
 * @returns {{id: string, kind: 'stop'|'departure'|'leg'|'return', start: number, end: number, step?: object, minutes?: number}[]}
 */
export function buildTimelineEvents(dayResult, mode = 'after') {
	if (!dayResult) return [];
	const isAfter = mode === 'after';
	const steps = isAfter
		? dayResult.steps
		: dayResult.steps
				.concat((dayResult.unplaceable || []).map((item) => ({ item, kind: STEP_KIND.VISIT, start: item.start, end: item.end, oldStart: item.start, oldEnd: item.end, moved: MOVE.UNCHANGED })))
				.map((s) => ({ ...s, start: s.oldStart, end: s.oldEnd, moved: MOVE.UNCHANGED, legBefore: dayResult.beforeLegs?.get?.(s.item.uid) ?? null }))
				.sort((a, b) => a.start - b.start || a.end - b.end);

	const events = steps.map((step) => ({ id: `stop-${step.item.uid}`, kind: 'stop', start: step.start, end: step.end, step }));
	const routed = steps.filter((s) => s.item.isStore && hasPoint(s.item));
	routed.forEach((step, index) => {
		const minutes = step.legBefore || 0;
		if (!minutes) return;
		if (index === 0) events.push({ id: `departure-${step.item.uid}`, kind: 'departure', start: step.start - minutes * MINUTE, end: step.start, minutes });
		else events.push({ id: `leg-${step.item.uid}`, kind: 'leg', start: routed[index - 1].end, end: routed[index - 1].end + minutes * MINUTE, minutes });
	});
	const endLeg = isAfter ? dayResult.endLeg : dayResult.beforeEndLeg;
	const last = routed[routed.length - 1];
	if (last && endLeg) events.push({ id: `return-${last.item.uid}`, kind: 'return', start: last.end, end: last.end + endLeg * MINUTE, minutes: endLeg });

	return events.sort((a, b) => a.start - b.start);
}

/**
 * Optimizes every day of the period independently (RG-02, RG-19) and totals the travel time.
 */
export function optimizePeriod(days, durations, options = {}, settings = {}) {
	const results = days.map((day) => optimizeDay(day, durations, options, settings));
	const travelBefore = results.reduce((sum, r) => sum + r.travelBefore, 0);
	const travelAfter = results.reduce((sum, r) => sum + r.travelAfter, 0);
	return {
		days: results,
		travelBefore,
		travelAfter,
		gain: travelBefore - travelAfter,
		changes: results.flatMap((r) => r.changes),
		unplaceable: results.flatMap((r) => r.unplaceable),
		optimizedDays: results.filter((r) => r.changes.length > 0).length,
	};
}
